import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import { Send } from 'lucide-react';
import { getPublishedReviews, submitReview } from '../supabase';

const STATIC_REVIEWS = [
  { initials:'TM', bg:'#0F52BA', name:'T.M.', level:"Master's Student", country:'🇲🇾 Malaysia', service:'Thesis Structure', stars:5, text:"Support with my master's thesis was organised and easy to follow. The chapters were aligned with my research objectives, and recommendations helped me respond to my supervisor's comments more confidently." },
  { initials:'PR', bg:'#7C3AED', name:'P.R.', level:'Postgraduate Researcher', country:'🇰🇪 Kenya', service:'SPSS Data Analysis', stars:5, text:'I had collected questionnaire data but was unsure which statistical tests to use. The dataset was cleaned, the analysis conducted in SPSS, and every table explained in clear academic language.' },
  { initials:'AO', bg:'#DC2626', name:'A.O.', level:'Doctoral Candidate', country:'🇳🇬 Nigeria', service:'Dissertation Corrections', stars:5, text:'My dissertation had extensive examiner corrections covering several chapters. Each comment was addressed systematically without unnecessarily rewriting the entire study.' },
  { initials:'SN', bg:'#0D9488', name:'S.N.', level:'Qualitative Researcher', country:'🇸🇬 Singapore', service:'NVivo Thematic Analysis', stars:5, text:'The NVivo analysis was detailed and well organised. Interview responses were coded into meaningful themes and subthemes, with relevant participant quotations included in the findings.' },
  { initials:'WC', bg:'#FF7A00', name:'W.C.', level:'Business Student', country:'🇭🇰 Hong Kong', service:'Excel Analysis', stars:5, text:'I needed my raw Excel data converted into clear tables, charts, and summaries. The final analysis was accurate, professionally arranged, and much easier to present in my report.' },
  { initials:'FK', bg:'#16a34a', name:'F.K.', level:"Master's Student", country:'🇮🇩 Indonesia', service:'Literature Review', stars:5, text:'The literature review was well structured around the study objectives rather than a collection of unrelated summaries. Research gaps were clearly identified and sources properly referenced.' },
  { initials:'BM', bg:'#6D28D9', name:'B.M.', level:'Undergraduate Researcher', country:'🇹🇿 Tanzania', service:'Research Proposal', stars:5, text:'My proposal initially lacked a clear connection between the problem statement, objectives, questions, and methodology. The guidance helped strengthen alignment and made it much more coherent.' },
  { initials:'LT', bg:'#0891B2', name:'L.T.', level:'University Student', country:'🇻🇳 Vietnam', service:'Editing & Proofreading', stars:5, text:'The editing improved grammar, academic tone, paragraph flow, and referencing without changing the original meaning of my work. The document became clearer and more professional.' },
  { initials:'AM', bg:'#BE185D', name:'A.M.', level:'Nursing Student', country:'🇵🇭 Philippines', service:'Nursing Assignment', stars:5, text:'The nursing paper followed the instructions closely and used relevant academic evidence. The discussion connected theory with clinical practice in a clear, professional style.' },
  { initials:'JK', bg:'#EA580C', name:'J.K.', level:'MBA Student', country:'🇹🇭 Thailand', service:'Business Case Study', stars:5, text:'The case study went beyond describing the company. It applied the required business models, evaluated challenges, and provided practical recommendations supported by evidence.' },
  { initials:'EO', bg:'#475569', name:'E.O.', level:'Engineering Student', country:'🇬🇭 Ghana', service:'Engineering Report', stars:5, text:'The technical report was well organised, with clear calculations, tables, figures, and explanations. Corrections were also completed promptly after additional comments from my lecturer.' },
  { initials:'CW', bg:'#0F52BA', name:'C.W.', level:'Postgraduate Student', country:'🇨🇳 China', service:'Referencing & Formatting', stars:5, text:'My document had inconsistent citations, table formats, headings, and reference entries. Everything was standardised according to APA 7th edition and looked professionally prepared.' },
  { initials:'RN', bg:'#7C3AED', name:'R.N.', level:'Research Student', country:'🇯🇵 Japan', service:'Revision Support', stars:5, text:'What impressed me most was the responsiveness after delivery. My supervisor requested several small changes and the revisions were handled carefully while maintaining consistency throughout.' },
  { initials:'KS', bg:'#16a34a', name:'K.S.', level:'Doctoral Researcher', country:'🇰🇷 South Korea', service:'Mixed-Methods Research', stars:5, text:'I received support with both quantitative survey analysis and qualitative interview findings. The two sets of results were presented separately and integrated clearly in the discussion.' },
  { initials:'YA', bg:'#DC2626', name:'Y.A.', level:'Undergraduate Student', country:'🇺🇬 Uganda', service:'Essay Editing', stars:5, text:'The essay was improved significantly in terms of argument, structure, transitions, and use of evidence. The feedback helped me understand where my original draft needed strengthening.' },
];

const SERVICE_FILTERS = ['All','Thesis','Dissertation','Data Analysis','Essay','Case Study','Research Proposal','Editing'];

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
      {[1,2,3,4,5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange && onChange(n)}
          onMouseEnter={() => onChange && setHover(n)}
          onMouseLeave={() => onChange && setHover(0)}
          style={{
            background:'none', border:'none', cursor: onChange ? 'pointer' : 'default',
            padding:'2px', fontSize:28, lineHeight:1,
            color: n <= (hover || value) ? '#FFB300' : '#ddd',
            transition:'color 0.1s',
          }}
        >★</button>
      ))}
    </div>
  );
}

export default function ReviewsPage({ navigate }) {
  const [filter, setFilter]   = useState('All');
  const [dbReviews, setDbReviews] = useState([]);
  const [form, setForm]       = useState({ name:'', level:'', country:'', service:'', rating:5, text:'' });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    getPublishedReviews()
      .then(({ data }) => { if (data?.length) setDbReviews(data); })
      .catch(() => {});
  }, []);

  const allReviews = [
    ...dbReviews.map(r => ({
      initials: (r.student_name || '?').slice(0,2).toUpperCase(),
      bg: '#0F52BA',
      name: r.student_name || 'Anonymous',
      level: r.academic_level || '',
      country: r.country || '',
      service: r.service || '',
      stars: r.rating || 5,
      text: r.review_text || '',
    })),
    ...STATIC_REVIEWS,
  ];

  const filtered = filter === 'All'
    ? allReviews
    : allReviews.filter(r => r.service.toLowerCase().includes(filter.toLowerCase()));

  const set = k => e => { setError(''); setForm(f => ({ ...f, [k]: e.target.value })); };

  const handleSubmit = async () => {
    setError('');
    if (!form.name.trim()) { setError('Please enter your name or initials.'); return; }
    if (!form.text.trim() || form.text.trim().split(/\s+/).length < 8) {
      setError('Please write at least 8 words describing your experience.');
      return;
    }
    setSaving(true);
    try {
      const { error: err } = await submitReview({
        student_name:   form.name.trim(),
        academic_level: form.level || null,
        country:        form.country.trim() || null,
        service:        form.service.trim() || null,
        rating:         form.rating,
        review_text:    form.text.trim(),
        is_published:   false,
      });
      if (err) throw err;
      setSubmitted(true);
      setForm({ name:'', level:'', country:'', service:'', rating:5, text:'' });
    } catch(e) {
      setError(`Could not submit: ${e.message}. Please try WhatsApp instead.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <div className="page-hero">
        <div className="section-label">Client Testimonials</div>
        <h1>What Our Clients Say</h1>
        <p style={{ maxWidth:520, margin:'0 auto' }}>
          Committed to clear communication, subject-specific guidance, timely delivery, and responsive revision support.
        </p>
        <div className="review-stats" style={{ display:'flex', justifyContent:'center', flexWrap:'wrap', gap:16, marginTop:24 }}>
          {[['4.9/5','Average Rating'],['6,000+','Happy Clients'],['98%','Would Recommend']].map(([v,l]) => (
            <div key={l} style={{ background:'rgba(255,255,255,0.1)', borderRadius:12, padding:'14px 24px', textAlign:'center', minWidth:100 }}>
              <div style={{ fontFamily:'Sora,sans-serif', fontSize:'1.5rem', fontWeight:800, color:'#fff' }}>{v}</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.6)', marginTop:4 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      <section style={{ padding:'48px 5%' }}>
        {/* Filter chips */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:28 }}>
          {SERVICE_FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding:'7px 14px', borderRadius:20, border:`1.5px solid ${filter===f?'var(--blue)':'var(--border)'}`,
              background:filter===f?'var(--blue)':'#fff', color:filter===f?'#fff':'var(--text-muted)',
              fontSize:13, fontWeight:600, cursor:'pointer', transition:'all 0.2s', fontFamily:'Inter,sans-serif',
            }}>{f}</button>
          ))}
        </div>

        {/* Reviews grid — responsive */}
        <div style={{
          display:'grid',
          gridTemplateColumns:'repeat(auto-fill,minmax(min(100%,300px),1fr))',
          gap:16, marginBottom:56,
        }}>
          {filtered.map((r,i) => (
            <div key={i} style={{
              background:'#fff', border:'1px solid var(--border)', borderRadius:14,
              padding:20, display:'flex', flexDirection:'column', gap:12,
            }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                <div style={{
                  width:42, height:42, borderRadius:'50%', background:r.bg,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  color:'#fff', fontWeight:700, fontSize:14, flexShrink:0,
                }}>{r.initials}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'var(--text)' }}>{r.name}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>
                    {r.level}{r.level && r.country ? ' · ' : ''}{r.country}
                  </div>
                  <div style={{ color:'#FFB300', fontSize:14, letterSpacing:1, marginTop:2 }}>
                    {'★'.repeat(r.stars)}{'☆'.repeat(5-r.stars)}
                  </div>
                </div>
                {r.service && (
                  <span style={{
                    fontSize:10, fontWeight:700, background:'var(--gray)', color:'var(--text-muted)',
                    padding:'3px 7px', borderRadius:5, whiteSpace:'nowrap', flexShrink:0, alignSelf:'flex-start',
                  }}>{r.service}</span>
                )}
              </div>
              <p style={{ fontSize:13.5, color:'var(--text-muted)', lineHeight:1.7, margin:0 }}>{r.text}</p>
            </div>
          ))}
        </div>

        {/* Submit review form */}
        <div style={{ background:'var(--gray)', borderRadius:20, padding:'clamp(24px,5vw,40px)' }}>
          <div style={{ textAlign:'center', marginBottom:28 }}>
            <div className="section-label text-center">Share Your Experience</div>
            <h2 className="section-title text-center" style={{ fontSize:'clamp(1.2rem,4vw,1.7rem)' }}>Leave a Review</h2>
            <p style={{ color:'var(--text-muted)', fontSize:14, maxWidth:500, margin:'0 auto', lineHeight:1.6 }}>
              Have you received support from our team? Your feedback helps other students make informed decisions.
            </p>
          </div>

          {submitted ? (
            <div style={{ textAlign:'center', padding:'32px 16px' }}>
              <div style={{ fontSize:52, marginBottom:12 }}>🙏</div>
              <h3 style={{ fontFamily:'Sora,sans-serif', fontSize:'1.1rem', fontWeight:700, marginBottom:8 }}>Thank You!</h3>
              <p style={{ color:'var(--text-muted)', fontSize:14, marginBottom:20 }}>
                Your review has been submitted and will be published after our team verifies it.
              </p>
              <button className="btn-primary" onClick={() => setSubmitted(false)}>Write Another</button>
            </div>
          ) : (
            <div style={{ maxWidth:620, margin:'0 auto' }}>
              {/* Row 1 */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:14, marginBottom:14 }}>
                <div className="form-group" style={{ margin:0 }}>
                  <label>Your Name or Initials *</label>
                  <input
                    type="text"
                    placeholder="e.g. J.K. or James"
                    value={form.name}
                    onChange={set('name')}
                    style={{ fontSize:16 }} /* prevents iOS zoom */
                  />
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label>Academic Level</label>
                  <select value={form.level} onChange={set('level')} style={{ fontSize:16 }}>
                    {['','Undergraduate',"Master's Student",'Doctoral Candidate','Postgraduate Researcher','Professional'].map(o=>(
                      <option key={o} value={o}>{o || 'Select...'}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Row 2 */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:14, marginBottom:14 }}>
                <div className="form-group" style={{ margin:0 }}>
                  <label>Country</label>
                  <input type="text" placeholder="e.g. Malaysia" value={form.country} onChange={set('country')} style={{ fontSize:16 }}/>
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label>Service Received</label>
                  <input type="text" placeholder="e.g. Dissertation Support" value={form.service} onChange={set('service')} style={{ fontSize:16 }}/>
                </div>
              </div>
              {/* Rating */}
              <div className="form-group" style={{ margin:'0 0 14px' }}>
                <label>Your Rating</label>
                <StarRating value={form.rating} onChange={v => setForm(f => ({ ...f, rating:v }))}/>
              </div>
              {/* Text */}
              <div className="form-group" style={{ margin:'0 0 14px' }}>
                <label>Your Experience *</label>
                <textarea
                  placeholder="Describe your experience with our academic support team (at least 8 words)..."
                  value={form.text}
                  onChange={set('text')}
                  rows={5}
                  style={{ fontSize:16, resize:'vertical', minHeight:110 }}
                />
              </div>
              {/* Error */}
              {error && (
                <div style={{ background:'rgba(220,38,38,0.08)', border:'1px solid rgba(220,38,38,0.2)', borderRadius:8, padding:'10px 14px', color:'#DC2626', fontSize:13, marginBottom:14 }}>
                  ⚠️ {error}
                </div>
              )}
              {/* Submit */}
              <div style={{ textAlign:'center' }}>
                <button
                  className="btn-primary"
                  style={{ padding:'13px 36px', fontSize:15, opacity:saving?0.7:1, width:'100%', maxWidth:320, justifyContent:'center' }}
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  <Send size={16}/> {saving ? 'Submitting…' : 'Submit Review'}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer navigate={navigate}/>
    </div>
  );
}
